import { StateCreator, create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { useAuthStore } from './authStore';

interface HubSessionState {
  isHubSession: boolean;
  // The mirror user the handoff logged in as. The banner keys off a match
  // with the CURRENT authConfig user — a later normal login on this origin
  // must not inherit a stale hub banner.
  mirrorUserId: string | null;
  enteredAt: number | null;
  startHubSession: (mirrorUserId: string) => void;
  clearHubSession: () => void;
}

type HubSessionPersist = (
  config: StateCreator<HubSessionState>,
  options: PersistOptions<HubSessionState>,
) => StateCreator<HubSessionState>;

export const useHubSessionStore = create<HubSessionState>(
  (persist as HubSessionPersist)(
    (set) => ({
      isHubSession: false,
      mirrorUserId: null,
      enteredAt: null,
      startHubSession: (mirrorUserId) =>
        set({ isHubSession: true, mirrorUserId, enteredAt: Date.now() }),
      clearHubSession: () => set({ isHubSession: false, mirrorUserId: null, enteredAt: null }),
    }),
    { name: 'hubSessionStore' },
  ),
);

/**
 * True only while the CURRENT session is the hub handoff's mirror session.
 * The server-verified `is_hub_admin_mirror` (S13 / A4) is the primary
 * signal; the client-set store (written by HubTransition) stays OR'd in so
 * a session established before the auth payload refresh still banners.
 */
export const useIsActiveHubSession = (): boolean => {
  const { authConfig } = useAuthStore();
  const { isHubSession, mirrorUserId } = useHubSessionStore();

  if (!authConfig?.authtoken) return false;
  if (authConfig.user?.is_hub_admin_mirror === true) return true;

  return isHubSession && authConfig?.user?._id === mirrorUserId;
};
